import { useState } from "react";
import { isNumeric } from "../lib/format.ts";
import type { BlockDef, CollectionDef } from "../lib/types.ts";

type Any = Record<string, unknown>;

/** Drops keys whose value is undefined, so the block stays valid for the schema. */
const clean = (b: Any) => Object.fromEntries(Object.entries(b).filter(([, v]) => v !== undefined)) as unknown as BlockDef;

export function Props({
  block,
  blocks,
  collections,
  onChange,
}: {
  block: BlockDef;
  blocks: BlockDef[];
  collections: CollectionDef[];
  onChange: (b: BlockDef) => void;
}) {
  const b = block as BlockDef & Any;
  const set = (patch: Any) => onChange(clean({ ...b, ...patch }));
  const c = "collection" in block ? collections.find((x) => x.name === block.collection) : undefined;

  function changeCollection(name: string) {
    const nc = collections.find((x) => x.name === name)!;
    // Field choices belong to the old collection: reset them.
    const reset: Any = { collection: name, columns: undefined, fields: undefined, filter: undefined, sort: undefined, field: undefined, source: undefined, cardTitle: undefined };
    if (block.type === "kanban") reset.groupBy = nc.fields.find((f) => f.interface === "select")?.name;
    if (block.type === "metric") reset.agg = "count";
    set(reset);
  }

  return (
    <div className="stack">
      <strong>{{ table: "Tabela", form: "Formulário", details: "Detalhe", metric: "Indicador", kanban: "Kanban", text: "Texto" }[block.type]}</strong>
      {block.type !== "text" && (
        <label className="fld">
          Título
          <input value={(b.title as string) ?? ""} onChange={(e) => set({ title: e.target.value.trim() ? e.target.value : undefined })} />
        </label>
      )}
      <div className="fld">
        Largura
        <span className="seg">
          {(["full", "half"] as const).map((w) => (
            <button key={w} type="button" aria-pressed={block.width === w} onClick={() => set({ width: w })}>
              {w === "full" ? "Inteira" : "Metade"}
            </button>
          ))}
        </span>
      </div>

      {"collection" in block && (
        <label className="fld">
          Coleção
          <select value={block.collection} onChange={(e) => changeCollection(e.target.value)}>
            {collections
              .filter((x) => block.type !== "kanban" || x.fields.some((f) => f.interface === "select"))
              .map((x) => (
                <option key={x.name} value={x.name}>
                  {x.label}
                </option>
              ))}
          </select>
        </label>
      )}

      {c && block.type === "table" && (
        <>
          <FieldChecks label="Colunas" c={c} value={block.columns} onChange={(v) => set({ columns: v })} />
          <label className="fld">
            Linhas por página
            <select value={block.pageSize ?? 10} onChange={(e) => set({ pageSize: Number(e.target.value) })}>
              {[5, 10, 20, 50].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </label>
          <SortPicker c={c} value={block.sort} onChange={(v) => set({ sort: v })} />
          <FilterEditor value={block.filter} onChange={(v) => set({ filter: v })} />
        </>
      )}

      {c && block.type === "form" && (
        <>
          <FieldChecks label="Campos" c={c} value={block.fields} onChange={(v) => set({ fields: v })} />
          <label className="fld">
            Texto do botão
            <input value={block.submitLabel ?? ""} placeholder="Salvar" onChange={(e) => set({ submitLabel: e.target.value.trim() ? e.target.value : undefined })} />
          </label>
        </>
      )}

      {c && block.type === "details" && (
        <>
          <label className="fld">
            Mostra o registro clicado em
            <select value={block.source ?? ""} onChange={(e) => set({ source: e.target.value || undefined })}>
              <option value="">Nenhuma tabela</option>
              {blocks
                .filter((x) => x.type === "table" && x.collection === block.collection)
                .map((x, i) => (
                  <option key={x.id} value={x.id}>
                    {x.title ?? `Tabela ${i + 1} (sem título)`}
                  </option>
                ))}
            </select>
          </label>
          <FieldChecks label="Campos" c={c} value={block.fields} onChange={(v) => set({ fields: v })} />
        </>
      )}

      {c && block.type === "metric" && (
        <>
          <label className="fld">
            Cálculo
            <select
              value={block.agg ?? "count"}
              onChange={(e) => {
                const agg = e.target.value;
                const num = c.fields.find(isNumeric);
                set(agg === "count" ? { agg, field: undefined } : { agg, field: block.field ?? num?.name });
              }}
            >
              <option value="count">Contagem</option>
              {c.fields.some(isNumeric) && (
                <>
                  <option value="sum">Soma</option>
                  <option value="avg">Média</option>
                  <option value="min">Mínimo</option>
                  <option value="max">Máximo</option>
                </>
              )}
            </select>
          </label>
          {block.agg && block.agg !== "count" && (
            <label className="fld">
              Campo
              <select value={block.field ?? ""} onChange={(e) => set({ field: e.target.value })}>
                {c.fields.filter(isNumeric).map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <FilterEditor value={block.filter} onChange={(v) => set({ filter: v })} />
        </>
      )}

      {c && block.type === "kanban" && (
        <>
          <label className="fld">
            Colunas por
            <select value={block.groupBy} onChange={(e) => set({ groupBy: e.target.value })}>
              {c.fields
                .filter((f) => f.interface === "select")
                .map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.label}
                  </option>
                ))}
            </select>
          </label>
          <label className="fld">
            Título do cartão
            <select value={block.cardTitle ?? ""} onChange={(e) => set({ cardTitle: e.target.value || undefined })}>
              <option value="">Campo título da coleção</option>
              {c.fields.map((f) => (
                <option key={f.name} value={f.name}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      {block.type === "text" && (
        <label className="fld">
          Conteúdo
          <textarea rows={6} value={block.content} onChange={(e) => set({ content: e.target.value })} />
        </label>
      )}
      <p className="muted" style={{ margin: 0, fontSize: 12 }}>
        Id do bloco: <code>{block.id}</code>
      </p>
    </div>
  );
}

function FieldChecks({ label, c, value, onChange }: { label: string; c: CollectionDef; value: string[] | undefined; onChange: (v: string[] | undefined) => void }) {
  const all = c.fields.map((f) => f.name);
  const on = value ?? all;
  return (
    <div className="fld">
      {label}
      <div className="checks">
        {c.fields.map((f) => {
          const checked = on.includes(f.name);
          return (
            <label key={f.name} className={checked ? "on" : ""}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {
                  const next = checked ? on.filter((n) => n !== f.name) : all.filter((n) => n === f.name || on.includes(n));
                  onChange(next.length ? next : undefined);
                }}
              />
              {f.label}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function SortPicker({ c, value, onChange }: { c: CollectionDef; value: string[] | undefined; onChange: (v: string[] | undefined) => void }) {
  const first = value?.[0] ?? "";
  const desc = first.startsWith("-");
  const field = first.replace(/^-/, "");
  return (
    <div className="fld">
      Ordenar por
      <div className="row" style={{ flexWrap: "nowrap" }}>
        <select value={field} onChange={(e) => onChange(e.target.value ? [`${desc ? "-" : ""}${e.target.value}`] : undefined)}>
          <option value="">Mais antigos primeiro</option>
          {c.fields.map((f) => (
            <option key={f.name} value={f.name}>
              {f.label}
            </option>
          ))}
        </select>
        <select value={desc ? "desc" : "asc"} disabled={!field} onChange={(e) => onChange([`${e.target.value === "desc" ? "-" : ""}${field}`])} style={{ width: 120 }}>
          <option value="asc">crescente</option>
          <option value="desc">decrescente</option>
        </select>
      </div>
    </div>
  );
}

/** The filter as JSON: the same syntax the AI uses (see the filtros guide). */
function FilterEditor({ value, onChange }: { value: Any | undefined; onChange: (v: Any | undefined) => void }) {
  const [text, setText] = useState(value ? JSON.stringify(value, null, 2) : "");
  const [bad, setBad] = useState(false);
  return (
    <label className={`fld ${bad ? "bad" : ""}`}>
      Filtro (JSON)
      <textarea
        rows={3}
        placeholder='{"status": "aberto"}'
        style={{ fontFamily: "var(--f-mono)", fontSize: 12 }}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value.trim()) {
            setBad(false);
            return onChange(undefined);
          }
          try {
            const v = JSON.parse(e.target.value) as unknown;
            const ok = !!v && typeof v === "object" && !Array.isArray(v);
            setBad(!ok);
            if (ok) onChange(v as Any);
          } catch {
            setBad(true);
          }
        }}
      />
      {bad && <span className="err">JSON inválido</span>}
    </label>
  );
}
